import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './AuthPage.css';
import { startNaverLogin } from './naverLogin';
import { linkedProviders, providerNames } from './socialProviders';

const SERVER = (process.env.REACT_APP_SERVER || '').replace(/\/$/, '');

const passwordValid = value => Array.from(value).length >= 8 && value.length <= 72 && !/\s/.test(value)
    && [/[a-z]/i, /[0-9]/, /[!-/:-@[-`{-~]/].filter(pattern => pattern.test(value)).length >= 2;
const mark = <span className="auth-required" aria-hidden="true">*</span>;
const genderOptions = [['male', '남'], ['female', '여']];

export default function AccountRecoveryPage({ mode }) {
    const findingEmail = mode === 'email';
    const [step, setStep] = useState('input');
    const [values, setValues] = useState({ name: '', birthDate: '', gender: '', email: '', code: '', password: '', confirm: '' });
    const [error, setError] = useState('');
    const [expiresAt, setExpiresAt] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [touched, setTouched] = useState({});
    const [pending, setPending] = useState(false);
    const [resetToken, setResetToken] = useState('');
    const [foundEmail, setFoundEmail] = useState('');
    const [foundProviders, setFoundProviders] = useState([]);
    const requestPending = useRef(false);

    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const date = new Date(`${values.birthDate}T00:00:00Z`);
    const birthValid = /^\d{4}-\d{2}-\d{2}$/.test(values.birthDate) && !Number.isNaN(date.getTime())
        && date.toISOString().slice(0, 10) === values.birthDate && values.birthDate >= '1900-01-01' && values.birthDate <= today;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()) && values.email.trim().length <= 254;
    const errors = {
        name: values.name.trim().length >= 2 && values.name.trim().length <= 50 ? '' : '이름을 2~50자로 입력해주세요.',
        birthDate: birthValid ? '' : '올바른 생년월일을 입력해주세요.',
        gender: genderOptions.some(([value]) => value === values.gender) ? '' : '성별을 선택해주세요.',
        email: emailValid ? '' : '올바른 이메일을 입력해주세요.',
        password: passwordValid(values.password) ? '' : '공백 없이 8자 이상, 영문·숫자·특수문자 중 2종류 이상을 포함해주세요.',
        confirm: values.confirm && values.confirm === values.password ? '' : '비밀번호가 일치하지 않아요',
    };
    
    useEffect(() => {
        if (step !== 'code') return undefined;
        const update = () => setSeconds(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
        update();
        const timer = setInterval(update, 1000);
        return () => clearInterval(timer);
    }, [step, expiresAt]);

    function change(event) {
        const { name, value } = event.target;
        setValues(previous => ({ ...previous, [name]: name === 'code' ? value.replace(/\D/g, '').slice(0, 6) : value }));
        setTouched(previous => ({ ...previous, [name]: true }));
        setError('');
    }
    function field(name) {
        return {
            name, value: values[name], onChange: change,
            onBlur: () => setTouched(previous => ({ ...previous, [name]: true })),
            'aria-invalid': Boolean(touched[name] && errors[name]),
            'aria-describedby': touched[name] && errors[name] ? `recovery-${name}-error` : undefined,
        };
    }
    const message = name => touched[name] && errors[name] && <small id={`recovery-${name}-error`} className="auth-field-error">{errors[name]}</small>;

    async function sendCode() {
        if (requestPending.current) return;
        requestPending.current = true;
        setPending(true);
        setError('');
        setValues(previous => ({ ...previous, code: '' }));
        try {
            await axios.post(`${SERVER}/api/auth/email/send`, { email: values.email.trim(), purpose: 'reset' }, { timeout: 45000 });
            setExpiresAt(Date.now() + 600000);
            setSeconds(600);
            setStep('code');
            window.alert('인증메일을 보냈습니다.');
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || '이메일 발송에 실패했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            requestPending.current = false;
            setPending(false);
        }
    }

    async function submit(event) {
        event.preventDefault();
        setError('');
        if (requestPending.current) return;

        if (step === 'input') {
            if (findingEmail) {
                setTouched({ name: true, birthDate: true, gender: true });
                if (errors.name || errors.birthDate || errors.gender) return;
                requestPending.current = true;
                setPending(true);
                try {
                    const { data } = await axios.post(`${SERVER}/api/auth/email/find`, { name: values.name.trim(), birthDate: values.birthDate, gender: values.gender }, { timeout: 45000 });
                    setFoundEmail(data.email);
                    setFoundProviders(linkedProviders(data.socialAccounts));
                    setStep('found');
                } catch (err) {
                    if (err.response?.status === 404) setStep('missing');
                    else setError(err.response?.data?.message || err.response?.data?.error || '요청에 실패했습니다. 잠시 후 다시 시도해주세요.');
                } finally {
                    requestPending.current = false;
                    setPending(false);
                }
            } else {
                setTouched({ email: true });
                if (errors.email) return;
                await sendCode();
            }
        } else if (step === 'code') {
            if (Date.now() >= expiresAt) { setSeconds(0); setError('인증번호가 만료되었습니다. 다시 발송해주세요.'); return; }
            if (!/^\d{6}$/.test(values.code)) return;
            requestPending.current = true;
            setPending(true);
            try {
                const { data } = await axios.post(`${SERVER}/api/auth/email/verify`, { email: values.email.trim(), code: values.code, purpose: 'reset' }, { timeout: 45000 });
                setResetToken(data.resetToken || '');
                setTouched({});
                setStep('password');
            } catch (err) {
                setError(err.response?.data?.message || err.response?.data?.error || '인증번호가 일치하지 않아요');
            } finally {
                requestPending.current = false;
                setPending(false);
            }
        } else if (step === 'password') {
            setTouched({ password: true, confirm: true });
            if (errors.password || errors.confirm) return;
            requestPending.current = true;
            setPending(true);
            try {
                await axios.post(`${SERVER}/api/auth/password/reset`, { email: values.email.trim(), resetToken, password: values.password }, { timeout: 45000 });
                setValues(previous => ({ ...previous, password: '', confirm: '', code: '' }));
                setStep('done');
            } catch (err) {
                setError(err.response?.data?.message || err.response?.data?.error || '비밀번호 변경에 실패했습니다. 다시 시도해주세요.');
            } finally {
                requestPending.current = false;
                setPending(false);
            }
        }
    }

    const disabled = pending || (step === 'input' ? (findingEmail ? Boolean(errors.name || errors.birthDate || errors.gender) : Boolean(errors.email))
        : step === 'code' ? values.code.length !== 6 || seconds === 0 : Boolean(errors.password || errors.confirm));
    return <div className="auth-page"><main className="auth-card">
        <span className="auth-kicker">GARAM MIND</span>
        <h1>{findingEmail ? '이메일 찾기' : '비밀번호 찾기'}</h1>
        <p>{findingEmail ? '가입할 때 입력한 이름, 생년월일, 성별을 입력해주세요.' : '이메일 인증 후 새 비밀번호를 설정해주세요.'}</p>
        {['input', 'code', 'password'].includes(step) && <form noValidate onSubmit={submit}>
            {step === 'input' && (findingEmail ? <>
                <label><span>이름 {mark}</span><input required {...field('name')} maxLength={50} autoComplete="name" />{message('name')}</label>
                <label><span>생년월일 {mark}</span><input required type="date" {...field('birthDate')} min="1900-01-01" max={today} autoComplete="bday" />{message('birthDate')}</label>
                <fieldset className="auth-radio-group" aria-describedby={touched.gender && errors.gender ? 'recovery-gender-error' : undefined}>
                    <legend>성별 {mark}</legend>
                    {genderOptions.map(([value, label]) => <label key={value}><input type="radio" name="gender" value={value} checked={values.gender === value} onChange={change} required />{label}</label>)}
                    {message('gender')}
                </fieldset>
            </> : <label><span>이메일 {mark}</span><input required type="email" {...field('email')} maxLength={254} autoComplete="email" />{message('email')}</label>)}
            {step === 'code' && <>
                <p>{values.email.trim()} 주소로 보낸 인증번호를 입력해주세요.</p>
                <label>인증번호<input required {...field('code')} aria-invalid={Boolean(error)} aria-describedby="recovery-timer" inputMode="numeric" autoComplete="one-time-code" maxLength={6} disabled={seconds === 0} /></label>
                <small id="recovery-timer" className={seconds === 0 ? 'auth-field-error' : undefined}>{seconds === 0 ? '유효시간이 만료되었습니다. 인증번호를 다시 발송해주세요.' : `유효시간 ${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`}</small>
                <div className="auth-recovery-actions"><button type="button" onClick={sendCode} disabled={pending}>인증번호 재발송</button><button type="button" onClick={() => { setStep('input'); setError(''); setValues(previous => ({ ...previous, code: '' })); }} disabled={pending}>이메일 다시 입력</button></div>
            </>}
            {step === 'password' && <>
                <label><span>새 비밀번호 {mark}</span><input required type="password" {...field('password')} maxLength={72} autoComplete="new-password" />{message('password')}</label>
                <small>공백 없이 8자 이상, 영문·숫자·특수문자 중 2종류 이상을 포함해주세요.</small>
                <label><span>새 비밀번호 확인 {mark}</span><input required type="password" {...field('confirm')} maxLength={72} autoComplete="new-password" />{message('confirm')}</label>
            </>}
            {error && <div className="auth-error" role="alert">{error}</div>}
            <button className="auth-submit" type="submit" disabled={disabled}>{step === 'input' ? findingEmail ? '이메일 찾기' : '인증번호 발송' : step === 'code' ? '인증 확인' : '비밀번호 변경'}</button>
        </form>}
        {step === 'found' && <section className="auth-recovery-result" role="status"><h2>가입한 이메일</h2><strong>{foundEmail}</strong><p>위 이메일로 로그인해주세요.</p>
            {foundProviders.length > 0 && <p className="auth-recovery-social">{providerNames(foundProviders)} 계정이 연동되어 있어 간편 로그인도 이용할 수 있습니다.</p>}
            {foundProviders.includes('naver') && <button type="button" onClick={() => startNaverLogin()}>네이버로 로그인</button>}
        </section>}
        {step === 'missing' && <section className="auth-recovery-result" role="status"><h2>가입 내역이 없습니다.</h2><p>입력한 이름과 생년월일로 가입한 계정을 찾을 수 없습니다.</p><button type="button" onClick={() => setStep('input')}>다시 찾기</button> <Link to="/register">회원가입</Link></section>}
        {step === 'done' && <section className="auth-recovery-result" role="status"><h2>비밀번호 변경 완료</h2><p>새로운 비밀번호로 로그인해주세요.</p></section>}
        <p className="auth-switch"><Link to="/login">로그인으로 돌아가기</Link></p>
    </main></div>;
}
AccountRecoveryPage.propTypes = { mode: PropTypes.oneOf(['email', 'password']).isRequired };
